import { Component, Input, OnInit, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import { OrderService } from '../service/order.service';
import { CashLoanService } from '../service/CashLoan.service';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { PaymentDetails } from '../Module/PayementDetails.model';
import { ClientService } from '../service/client.service';
import { forkJoin } from 'rxjs';
import { NotificationService } from '../service/notification.service';

@Component({
  selector: 'app-client-order-list',
  templateUrl: './client-order-list.component.html',
  styleUrls: ['./client-order-list.component.css']
})
export class ClientOrderListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() clientId!: number;

  combinedList: any[] = [];
  pagedList: any[] = [];

  currentPage = 1;
  itemsPerPage = 30;
  backendPageSize = 400;
  backendPageIndex = 0;

  totalItems = 0;
  pages: number[] = [];

  keyword: string = '';
  status: string = '';
  date: string = '';
  isLoading = false;
  initialTotalItems: any;
  private latestRequestId = 0;

  paymentDetails: PaymentDetails = new PaymentDetails(0, 0);
  allSelected = false;


  constructor(
    private orderService: OrderService,
    private cashLoanService: CashLoanService,
    private datePipe: DatePipe,
    private router: Router,
    private clientService: ClientService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    if (this.clientId) {
      this.fetchClientPaymentSummary();
      this.resetAndFetch();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && changes['clientId'].currentValue) {
      this.fetchClientPaymentSummary(); // ✅ Only fetch once per client
      this.resetAndFetch();
    }
  }

  fetchClientPaymentSummary(): void {
    this.clientService.getClientPaymentSummary(this.clientId).subscribe({
      next: (summary: PaymentDetails) => {
        this.paymentDetails = new PaymentDetails(summary.paid ?? 0, summary.unpaid ?? 0);
      },
      error: (err: any) => {
        console.error('Failed to fetch payment summary', err);
      }
    });
  }

  resetAndFetch(): void {
    this.backendPageIndex = 0;
    this.combinedList = [];
    this.currentPage = 1;
    this.getClientData(this.backendPageIndex);
  }

  getClientData(backendPage: number): void {
    if (!this.clientId) return;

    this.isLoading = true;
    const requestId = ++this.latestRequestId;

    this.orderService.getPagedOrdersForClient(this.clientId, backendPage, this.backendPageSize, this.keyword, this.status, this.date)
      .subscribe(orderData => {
        this.cashLoanService.getPagedLoansForClient(this.clientId, backendPage, this.backendPageSize, this.keyword, this.status, this.date)
          .subscribe(loanData => {
            if (requestId !== this.latestRequestId) return;

            const combined = [
              ...orderData.content.map((order: any) => ({ ...order, type: 'order' })),
              ...loanData.content.map((loan: any) => ({ ...loan, type: 'loan' }))
            ];

            combined.sort((a, b) =>
              new Date(b.orderDate || b.loanDate).getTime() - new Date(a.orderDate || a.loanDate).getTime()
            );

            this.combinedList = [...this.combinedList, ...combined];

            const currentTotal = orderData.totalElements + loanData.totalElements;

            if (!this.keyword && !this.status && !this.date && backendPage === 0) {
              this.initialTotalItems = currentTotal; // ✅ Only set on first unfiltered load
            }

            this.totalItems = currentTotal;
            this.pages = Array.from({ length: Math.ceil(this.totalItems / this.itemsPerPage) }, (_, i) => i + 1);

            this.updatePagedList();
            this.isLoading = false;
          }, () => this.isLoading = false);
      }, () => this.isLoading = false);
  }

  updatePagedList(): void {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;

    if (end > this.combinedList.length) {
      this.backendPageIndex++;
      this.getClientData(this.backendPageIndex);
    }

    this.pagedList = this.combinedList.slice(start, end);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.updatePagedList();
  }

  onInputChange(event: Event): void {
    this.keyword = (event.target as HTMLInputElement).value;
    this.saveFilterState();
    this.resetAndFetch();
  }

  onStatusChange(event: Event): void {
    this.status = (event.target as HTMLSelectElement).value;
    this.saveFilterState();
    this.resetAndFetch();
  }

  onDateChange(event: Event): void {
    this.date = (event.target as HTMLInputElement).value;
    this.saveFilterState();
    this.resetAndFetch();
  }

  saveFilterState(): void {
    localStorage.setItem('clientOrderFilters', JSON.stringify({
      keyword: this.keyword,
      status: this.status,
      date: this.date
    }));
  }

  isOrderLoanPaid(item: any): boolean {
    return item?.isPaid?.toString() === 'true';
  }

  formatDate(date: string | Date): string {
    return this.datePipe.transform(date, 'dd MMMM, yyyy', '', 'fr') ?? '';
  }

navigateToEdit(item: any): void {
  let url = '';

  if (item.type === 'order') {
    url = this.router.serializeUrl(
      this.router.createUrlTree(['/updateOrder'], { queryParams: { id: item.id } })
    );
  } else if (item.type === 'loan') {
    url = this.router.serializeUrl(
      this.router.createUrlTree(['/updateLoan'], { queryParams: { id: item.id } })
    );
  }

  if (url) {
    window.open(url, '_blank');
  }
}


  ngOnDestroy(): void {
    localStorage.removeItem('clientOrderFilters');
  }
  
toggleSelectAll(event: Event): void {
  const checked = (event.target as HTMLInputElement).checked;
  this.allSelected = checked;

  this.pagedList.forEach(item => {
    item.selected = checked;
  });

  this.logSelectedCount();
}

checkIfAllSelected(): void {
  this.allSelected = this.pagedList.every(item => item.selected);
  this.logSelectedCount();
}
logSelectedCount(): void {
  const selectedCount = this.pagedList.filter(item => item.selected).length;
  console.log(`Selected items: ${selectedCount}`);
}

paySelectedItems(): void {
  const selectedUnpaidOrders = this.pagedList.filter(
    item => item.selected && item.type === 'order' && !this.isOrderLoanPaid(item)
  );
  const selectedUnpaidLoans = this.pagedList.filter(
    item => item.selected && item.type === 'loan' && !this.isOrderLoanPaid(item)
  );

  const orderIds = selectedUnpaidOrders.map(o => o.id);
  const loanIds = selectedUnpaidLoans.map(l => l.id);

  const requests = [];

  if (orderIds.length > 0) {
    requests.push(this.orderService.bulkPayOrders(orderIds));
  }

  if (loanIds.length > 0) {
    requests.push(this.cashLoanService.bulkPayLoans(loanIds));
  }

  if (requests.length === 0) return;

  forkJoin(requests).subscribe({
    next: () => {
      this.notificationService.show('Paiement effectué avec succès');
      this.fetchClientPaymentSummary();
      this.resetAndFetch();
      this.allSelected = false; // ✅ Uncheck "Select All"
    },
    error: () => {
      this.notificationService.show('Erreur lors du paiement');
    }
  });
}
navigateTo(path: string): void {
  if (this.clientId) {
    console.log(`Navigating to ${path} with clientId: ${this.clientId}`);
    this.router.navigate([`/${path}`], {
      queryParams: { clientId: this.clientId }
    });
  }
}
goToOrderDetails(orderId: number): void {
  const url = this.router.serializeUrl(
    this.router.createUrlTree(['/orderDetails'], { queryParams: { id: orderId } })
  );
  window.open(url, '_blank');
}


getTooltip(item: any): string {
  const total = item.total ?? item.amount ?? 0;
  const advance = item.advancePayment ?? 0;
  const discount = item.discount ?? 0;
  if (this.isOrderLoanPaid(item) == false) {
    let unpaid = 0;

    if (item.type === 'order') {
      unpaid = total - discount - advance - (item.totalAbsorbedFromDebt ?? 0);
    } else if (item.type === 'loan') {
      unpaid = total - advance;
    }

    return `Montant impayé: ${unpaid.toFixed(2)} MAD`;
  } else {
    if (item.type === 'order') {
      return `Total: ${(total - discount).toFixed(2)} MAD`;
    } else if (item.type === 'loan') {
      return `Total: ${total.toFixed(2)}  MAD`;
    }
  }
  // Default return to satisfy return type
  return '';
}
getAdvanceTooltip(item: any): string {
  const advance = item.advancePayment ?? 0;
  return `${advance.toFixed(2)} MAD`;
}

}
